import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const apiUrl = import.meta.env.VITE_API_URL;

const ConstructionFooter = () => {
  const [footerData, setFooterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        const response = await fetch(`${apiUrl}/layout/footer/`);
        if (!response.ok) throw new Error('Failed to fetch footer data');
        const data = await response.json();
        setFooterData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchFooterData();
  }, []);

  if (loading) return <div className="py-10 text-center">Loading footer...</div>;
  if (error) return <div className="py-10 text-center text-red-500">Error: {error}</div>;
  if (!footerData) return null;

  // Social media icons component (same as before)
// Social media icons as SVG components
const SocialIcons = {
  facebook: (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>
    </svg>
  ),
  twitter: (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84"/>
    </svg>
  ),
  linkedin: (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
    </svg>
  ),
  instagram: (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
    </svg>
  )
};


  return (
    <footer className="bg-[#040404] text-white border-t-4 border-[#f9b414] relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#7bbf42]/10 blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-[#7bbf42] via-[#f9b414] to-[#7bbf42]"></div>
      
      <div className="container mx-auto px-4 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Company Info */}
          <div className="space-y-6">
            <div className="flex items-center">
              {footerData.logo ? (
                <img src={footerData.logo} alt="Company Logo" className="h-24 rounded-lg bg-white" />
              ) : (
                <span className="text-2xl font-bold">
                  <span className="text-white">BUILD</span>
                  <span className="text-[#f9b414]">MASTER</span>
                </span>
              )}
            </div>
            <p className="text-gray-300">{footerData.companyDescription}</p>

<div className="flex space-x-4">
  {footerData.socialLinks.map((social, index) => (
    <a 
      key={index}
      href={social.url} 
      target="_blank" 
      rel="noopener noreferrer"
      className="w-10 h-10 rounded-full bg-[#7bbf42] hover:bg-[#f9b414] text-white flex items-center justify-center transition-colors duration-300 hover:scale-110 transform"
      aria-label={social.platform}
    >
      {SocialIcons[social.platform] || (
        <span className="text-xs">{social.platform}</span>
      )}
    </a>
  ))}
</div>
          </div>

          {/* Office Locations */}
          <div className="md:col-span-2 lg:col-span-3">
            <h3 className="text-xl font-bold text-[#f9b414] border-b-2 border-[#7bbf42] pb-2 mb-6 inline-block">
              Our Office Locations
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {footerData.officeLocations.map((office, index) => (
                <div 
                  key={index} 
                  className="bg-[#0a0a0a] p-6 rounded-xl border border-[#7bbf42]/20 hover:border-[#f9b414]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#7bbf42]/10 group"
                >
                  <h4 className="text-lg font-semibold text-[#f9b414] mb-3 group-hover:text-white transition-colors">
                    {office.country}
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <div className="bg-[#7bbf42] p-1 rounded-full mr-3 flex-shrink-0 mt-1 group-hover:bg-[#f9b414] transition-colors">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                        </svg>
                      </div>
                      <p className="text-gray-300 text-sm leading-relaxed group-hover:text-white transition-colors">
                        {office.address}
                      </p>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-[#7bbf42] p-1 rounded-full mr-3 flex-shrink-0 mt-1 group-hover:bg-[#f9b414] transition-colors">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                        </svg>
                      </div>
                      <a 
                        href={`mailto:${office.email}`} 
                        className="text-gray-300 text-sm hover:text-[#7bbf42] group-hover:text-white transition-colors"
                      >
                        {office.email}
                      </a>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-[#7bbf42] p-1 rounded-full mr-3 flex-shrink-0 mt-1 group-hover:bg-[#f9b414] transition-colors">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                        </svg>
                      </div>
                      <a 
                        href={`tel:${office.phone}`} 
                        className="text-gray-300 text-sm hover:text-[#7bbf42] group-hover:text-white transition-colors"
                      >
                        {office.phone}
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-12 pt-6 border-t border-[#7bbf42]/20">
          <p className="text-gray-400 text-sm text-center">
            {footerData.copyrightText}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default ConstructionFooter;